/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { IDetailsGroupDividerProps } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CategoryRow, getTransformedTextToAnnotations } from 'src/clu/appShell/cluTestSetDetailsTabUtils';
import { LabeledEntities } from 'src/clu/core/services/trainedModels/cluModelVerificationSwagger';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

const messages = defineMessages({
    noPrediction: { id: 'clu.cluTestSetDetailsTableGroupHeader.noPrediction', defaultMessage: 'No Prediction' },
    unlabeled: { id: 'clu.cluTestSetDetailsTableGroupHeader.unlabeled', defaultMessage: 'Unlabeled in test set' },
    expandUtteranceButton: { id: 'clu.cluTestSetDetailsTableGroupHeader.expandUtteranceButton', defaultMessage: 'Expand utterance' },
    collapseUtteranceButton: { id: 'clu.cluTestSetDetailsTableGroupHeader.collapseUtteranceButton', defaultMessage: 'Collapse utterance' }
});

type CluTestSetDetailsTableGroupHeaderProps = {
    groupProps: IDetailsGroupDividerProps;
    isGroupExpanded: (utteranceId: string) => boolean;
    setExpandedUtterancesIds: (utteranceId: string[]) => void;
    expandedUtterancesIds: string[];
    renderStatusIcon: (hasConflict: boolean) => JSX.Element;
    showLabeledAnnotations: boolean;
};

const theme = getScopedTheme('CluTestSetDetailsPage');

export const CluTestSetDetailsTableGroupHeader = (props: CluTestSetDetailsTableGroupHeaderProps) => {
    const {
        groupProps,
        expandedUtterancesIds,
        showLabeledAnnotations,
        isGroupExpanded,
        renderStatusIcon,
        setExpandedUtterancesIds
    } = props;

    const { localize } = useLocalization();

    React.useEffect(() => {
        if (groupProps.groupIndex === 0) {
            setExpandedUtterancesIds([groupProps.group?.key]);
        }
    }, []);

    const onHandleRowExpansionClicked = () => {
        let newUtterances: string[] = [];
        if (isGroupExpanded(groupProps.group.key)) {
            newUtterances = expandedUtterancesIds.filter(j => j !== groupProps.group.key);
        } else {
            newUtterances = [...expandedUtterancesIds, groupProps.group.key];
        }
        setExpandedUtterancesIds(newUtterances);
    };

    const getAnnotations = (item: CategoryRow[], text: string) => {
        if (!text) return [];

        const predictedEntities = item
            ?.filter(i => i.kind === 'Entity' && i.entityPredictedAs.category !== localize(messages.noPrediction))
            .reduce<LabeledEntities[]>((acc, value) => [...acc, value.entityPredictedAs], []);

        const expectedEntities = item
            ?.filter(row => row.kind === 'Entity' && row.entityExpectedAs.category !== localize(messages.unlabeled))
            .reduce<LabeledEntities[]>((acc, value) => [...acc, value.entityExpectedAs], [])
            .filter(
                (e, index, arr) => arr.findIndex(a => a.category === e.category && a.offset === e.offset && a.length === e.length) === index
            );

        return !showLabeledAnnotations
            ? getTransformedTextToAnnotations(predictedEntities, true)
            : getTransformedTextToAnnotations(expectedEntities, false);
    };

    return (
        <Stack
            horizontal
            verticalAlign="baseline"
            tokens={{ childrenGap: 5 }}
            styles={{ root: { borderBottom: `1px solid ${theme.tableBorderColor}` } }}>
            <IconButton
                disabled={!groupProps.group.count}
                onClick={() => onHandleRowExpansionClicked()}
                styles={{ root: { background: theme.disabledIconBtnColor } }}
                ariaLabel={
                    isGroupExpanded(groupProps.group.key)
                        ? localize(messages.collapseUtteranceButton)
                        : localize(messages.expandUtteranceButton)
                }
                iconProps={{
                    iconName: isGroupExpanded(groupProps.group.key) ? 'ChevronDown' : 'ChevronRight'
                }}
                title={
                    isGroupExpanded(groupProps.group.key)
                        ? localize(messages.collapseUtteranceButton)
                        : localize(messages.expandUtteranceButton)
                }
            />
            {renderStatusIcon(groupProps.group?.data?.isGroupConflict)}
            <Labeler
                labelerOverflow="unset"
                text={groupProps?.group?.name}
                annotations={getAnnotations(groupProps?.group?.data?.items, groupProps?.group?.name)}
                labelerConfigs={{
                    isDisabled: true,
                    isRtl: isDocumentSwaggerRtl(groupProps?.group?.data?.language),
                    tokenizationType: isDocumentSwaggerWordTokenized(groupProps?.group?.data?.language) ? 'word' : 'character'
                }}
            />
        </Stack>
    );
};
